/**
 * Guard vision cones → per-tile blue field for the always-on cone highlight.
 *
 * This is the *tile-colouring* counterpart to `soundField.ts`: instead of laying
 * a translucent quad over each cone tile (the old `GuardVisionLayer`), the
 * renderer BRIGHTENS each affected tile glyph toward a bright blue (a SCREEN
 * blend — see `brightenTint`) by the cell intensity. Watched tiles glow brighter
 * than the surrounding floor, so danger reads as high-contrast rather than a
 * dark wash, while the glyph stays readable and the tile's own hue still shows
 * through (a red prop in a cone reads as a bright magenta, not a flat block).
 *
 * The region is the UNION of every rendered guard's FOV cone (the same
 * `fovTiles` the sim computes for perception), keeping the MAX intensity per
 * tile so overlapping cones don't darken their intersection. Only guards the
 * player can currently see (or has marked) project a cone — stealth intent: you
 * only know where a guard is looking when you can see the guard.
 *
 *   - Light modulates the cone per tile: a tile too dark for the guard to
 *     actually see is dropped (matches the sim — the guard can't perceive there).
 *   - Intensity is UNIFORM across the cone otherwise: a guard sees every
 *     non-occluded tile in its cone at the same clarity (the sim's `fovTiles` is
 *     a hard in/out set — there is no graded "less watched" far edge), so the
 *     tint doesn't fake a distance falloff.
 *   - **Clipped to the player's own vision.** In normal play only the
 *     INTERSECTION of a guard's cone with the player's current FOV is emitted —
 *     a guard watching tiles the player can't see is NOT revealed there, even
 *     when the player can see the guard. You learn which tiles *you can see* are
 *     dangerous, nothing beyond your own sight.
 *   - `showAll` (the debug toggle) drops that clip AND includes every lit
 *     guard's cone (not only visible ones), so the fog reveals the whole floor's
 *     cones for tuning (the debug quad overlay in perceptionOverlay draws on top).
 *
 * No PixiJS here — pure data. See CLAUDE.md > Vision / Rendering and rule 4.
 */

import { CONFIG } from '../config';
import type { FovMemory } from '../sim/fovMemory';
import type { Guard } from '../sim/guard';
import { isUnlit, lightLevelAt } from '../sim/light';
import type { MapData } from '../sim/map';

/** Per-tile guard-vision contribution. */
export interface VisionFieldCell {
  /** 0..1 strength — how far to brighten the tile toward blue (screen blend). */
  intensity: number;
  /** The bright blue the tile is brightened toward. */
  color: number;
}

/** Bright, saturated blue the cone tiles are brightened (screened) toward. */
const VISION_BLUE = 0x4ab8ff;
/** Base intensity for a guard the player can see vs. one only marked/tracked. */
const ALPHA_LIVE = 0.7;
const ALPHA_MARKED = 0.4;
/** A cell fainter than this isn't worth tinting / revealing. */
export const VISION_REVEAL_MIN = 0.03;

/**
 * Build the guard-vision field for the current frame.
 *
 * @param showAll when true (debug overlay on) EVERY lit guard's cone is
 *   included — not only the ones the player can currently see — so the fog
 *   reveals the whole floor's cones for the debug quad overlay. In normal play
 *   it's false: only visible / marked guards project a cone (stealth intent).
 */
export function computeVisionField(
  guards: readonly Guard[],
  playerFov: FovMemory,
  map: MapData,
  showAll: boolean,
): Map<number, VisionFieldCell> {
  const out = new Map<number, VisionFieldCell>();
  for (const g of guards) {
    if (g.body.dead || !g.fovTiles) continue;
    // A guard in the dark isn't drawn, so neither is its cone.
    if (isUnlit(map, g.x, g.y)) continue;
    const gIdx = g.y * map.width + g.x;
    const inPlayerFov = playerFov.isCurrentlyVisible(gIdx);
    // Normal play: only visible / marked guards project. Debug (`showAll`):
    // every lit guard, so the fog reveals the whole floor's cones.
    if (!showAll && !inPlayerFov && !g.marked) continue;
    // Uniform per-guard strength — a guard sees its whole cone at one clarity.
    const baseAlpha = inPlayerFov || showAll ? ALPHA_LIVE : ALPHA_MARKED;
    for (const tileIdx of g.fovTiles) {
      // Clip to the player's own vision: outside the debug `showAll`, a cone
      // tile the player can't currently see is not rendered — only the
      // intersection of the two cones shows.
      if (!showAll && !playerFov.isCurrentlyVisible(tileIdx)) continue;
      const tx = tileIdx % map.width;
      const ty = Math.floor(tileIdx / map.width);
      const lightMul = Math.min(1, lightLevelAt(map, tx, ty) / CONFIG.light.fullAt);
      if (lightMul <= 0) continue; // guard can't actually see this dark tile
      const intensity = baseAlpha * lightMul;
      if (intensity <= VISION_REVEAL_MIN) continue;
      const prior = out.get(tileIdx);
      if (!prior) {
        out.set(tileIdx, { intensity, color: VISION_BLUE });
      } else if (intensity > prior.intensity) {
        prior.intensity = intensity;
      }
    }
  }
  return out;
}
